import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api, API } from "../lib/api";
import { toast } from "sonner";
import { CalendarCheck, CheckCircle2, Upload, Image as ImageIcon, X, Loader2, Ruler, Palette } from "lucide-react";

const STYLES = [
  "Cinematic — dark & immersive",
  "Modern minimalist",
  "Classic warm",
  "Concealed / in-wall",
  "Outdoor / patio",
  "Not sure yet — advise me",
];

export default function Consultation() {
  const [f, setF] = useState({
    name: "",
    email: "",
    phone: "",
    room_length: "",
    room_width: "",
    room_height: "",
    style: "",
    message: "",
    image_url: "",
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef();

  const uploadImage = async (fl) => {
    if (!fl) return;
    if (!fl.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    if (fl.size > 5 * 1024 * 1024) { toast.error("Image must be under 5MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", fl);
      const r = await api.post("/consultations/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${process.env.REACT_APP_BACKEND_URL}${r.data.url}`;
      setF((p) => ({ ...p, image_url: url }));
      toast.success("Photo attached");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email || !f.phone) {
      toast.error("Please share your name, email and phone so we can respond.");
      return;
    }
    setLoading(true);
    try {
      const dimensions = [f.room_length, f.room_width, f.room_height].filter(Boolean).join(" × ");
      const details = {
        room_dimensions: dimensions,
        room_length: f.room_length,
        room_width: f.room_width,
        room_height: f.room_height,
        style: f.style,
        image_url: f.image_url,
      };
      const summary = [
        dimensions && `Room: ${dimensions} ft`,
        f.style && `Style: ${f.style}`,
        f.image_url && `Photo attached: ${f.image_url}`,
      ].filter(Boolean).join("\n");
      await api.post("/inquiries", {
        name: f.name,
        email: f.email,
        phone: f.phone,
        subject: "Consultation booking",
        message: [summary, f.message].filter(Boolean).join("\n\n"),
        kind: "consultation",
        details,
      });
      setDone(true);
      toast.success("Consultation booked — we'll be in touch within 24 hours.");
    } catch {
      toast.error("Something went wrong. Please call us at +91 80189 11999.");
    } finally { setLoading(false); }
  };

  const Chip = ({ active, children, ...rest }) => (
    <button type="button" className={`px-4 py-2 rounded-full text-sm border transition ${active ? "text-white" : "hover:opacity-80"}`}
      style={active ? { background: "#8A6A0F", borderColor: "#8A6A0F" } : { borderColor: "rgba(15,12,8,0.14)", color: "rgba(15,12,8,0.75)" }} {...rest}>
      {children}
    </button>
  );

  if (done) {
    return (
      <div className="pt-32 pb-32 container-x max-w-2xl mx-auto text-center" data-testid="consultation-success">
        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: "rgba(138,106,15,0.15)", color: "#8A6A0F" }}>
          <CheckCircle2 className="w-8 h-8" />
        </div>
        <div className="num-badge mb-4 justify-center inline-flex"><span>Consultation Booked</span></div>
        <h1 className="font-display leading-[0.95] tracking-[-0.02em]" style={{ fontSize: "clamp(2.5rem, 5vw, 4rem)", fontWeight: 400 }}>
          Thank you, <em className="gold-text" style={{ fontWeight: 500 }}>{f.name.split(" ")[0] || "friend"}.</em>
        </h1>
        <p className="mt-6 max-w-md mx-auto leading-relaxed" style={{ color: "rgba(15,12,8,0.65)", fontFamily: "'Manrope', sans-serif" }}>
          Our design specialist will reach out on <span style={{ color: "var(--ink)" }}>{f.phone}</span> or <span style={{ color: "var(--ink)" }}>{f.email}</span> within one business day to schedule a showroom visit or in-home survey.
        </p>
        <div className="mt-10 flex gap-3 justify-center flex-wrap">
          <Link to="/packages" className="btn-gold">Explore Packages</Link>
          <Link to="/shop" className="btn-ghost-outline">Continue Browsing</Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="consultation-page">
      {/* Hero banner — same photograph as the Free Consultation section on Home */}
      <section className="relative overflow-hidden grain on-dark" style={{ minHeight: "520px" }}>
        <img
          src="https://images.unsplash.com/photo-1614624532983-4ce03382d63d?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(10,9,7,0.88) 0%, rgba(10,9,7,0.68) 55%, rgba(10,9,7,0.25) 100%)" }}></div>
        <div className="container-x relative z-10 pt-40 pb-20 max-w-3xl">
          <div className="num-badge mb-6" style={{ color: "#C9A34A" }}>
            <span style={{ background: "#C9A34A" }}></span>
            <span style={{ color: "#C9A34A" }}>Book a Consultation · Free</span>
          </div>
          <h1 className="font-display leading-[0.95] tracking-[-0.02em] text-white" style={{ fontSize: "clamp(3rem, 6.5vw, 6.5rem)", fontWeight: 400 }}>
            Design your <em className="gold-text" style={{ fontWeight: 500 }}>room.</em>
          </h1>
          <p className="mt-8 max-w-xl leading-[1.75] font-light" style={{ color: "rgba(245,239,223,0.85)", fontFamily: "'Manrope', sans-serif" }}>
            Share your space and taste — our specialist will call you within one business day to schedule a showroom visit or an in-home survey. <em style={{ color: "#E8D68C" }}>No obligation.</em>
          </p>
        </div>
      </section>

      <div className="container-x pt-14 pb-24">
        <div className="grid lg:grid-cols-3 gap-8">
          <form onSubmit={submit}
            className="lg:col-span-2 glass rounded-3xl p-8 md:p-10 space-y-8"
            data-testid="consultation-form">

          {/* Contact */}
          <section className="space-y-4">
            <div className="num-badge"><span>01 — Contact</span></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label htmlFor="c-name" className="block text-xs uppercase tracking-[0.18em] mb-2" style={{ color: "rgba(15,12,8,0.5)" }}>Full name *</label>
                <input id="c-name" type="text" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Your name" className="w-full" data-testid="cons-name" />
              </div>
              <div>
                <label htmlFor="c-email" className="block text-xs uppercase tracking-[0.18em] mb-2" style={{ color: "rgba(15,12,8,0.5)" }}>Email *</label>
                <input id="c-email" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="you@example.com" className="w-full" data-testid="cons-email" />
              </div>
            </div>
            <div>
              <label htmlFor="c-phone" className="block text-xs uppercase tracking-[0.18em] mb-2" style={{ color: "rgba(15,12,8,0.5)" }}>Phone *</label>
              <input id="c-phone" type="tel" required value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="+91 —" className="w-full" data-testid="cons-phone" />
            </div>
          </section>

          <div className="hairline"></div>

          {/* Room */}
          <section className="space-y-4">
            <div className="num-badge flex items-center gap-2"><Ruler className="w-3 h-3" style={{ color: "#8A6A0F" }} /><span>02 — Room dimensions</span></div>
            <div className="text-xs" style={{ color: "rgba(15,12,8,0.55)", fontFamily: "'Manrope', sans-serif" }}>Approximate is fine — in feet.</div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] mb-2" style={{ color: "rgba(15,12,8,0.5)" }}>Length (ft)</label>
                <input type="number" placeholder="e.g. 18" value={f.room_length} onChange={(e) => setF({ ...f, room_length: e.target.value })} className="w-full" data-testid="cons-length" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] mb-2" style={{ color: "rgba(15,12,8,0.5)" }}>Width (ft)</label>
                <input type="number" placeholder="e.g. 14" value={f.room_width} onChange={(e) => setF({ ...f, room_width: e.target.value })} className="w-full" data-testid="cons-width" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] mb-2" style={{ color: "rgba(15,12,8,0.5)" }}>Height (ft)</label>
                <input type="number" placeholder="e.g. 10" value={f.room_height} onChange={(e) => setF({ ...f, room_height: e.target.value })} className="w-full" data-testid="cons-height" />
              </div>
            </div>
          </section>

          <div className="hairline"></div>

          {/* Style */}
          <section>
            <div className="num-badge mb-4 flex items-center gap-2"><Palette className="w-3 h-3" style={{ color: "#8A6A0F" }} /><span>03 — Preferred theatre style</span></div>
            <div className="flex flex-wrap gap-2">
              {STYLES.map((s) => (
                <Chip key={s} active={f.style === s} onClick={() => setF({ ...f, style: s })} data-testid={`cons-style-${s}`}>
                  {s}
                </Chip>
              ))}
            </div>
          </section>

          <div className="hairline"></div>

          {/* Optional photo */}
          <section>
            <div className="num-badge mb-4"><span>04 — Room photo (optional)</span></div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0])} data-testid="cons-file-input" />
            {f.image_url ? (
              <div className="flex items-center gap-4 rounded-xl p-3 border" style={{ borderColor: "rgba(15,12,8,0.1)" }}>
                <div className="w-20 h-20 rounded-lg overflow-hidden shrink-0" style={{ background: "rgba(15,12,8,0.05)" }}>
                  <img src={f.image_url} alt="room" className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium" style={{ color: "var(--ink)" }}>Photo attached</div>
                  <div className="text-xs mt-1" style={{ color: "rgba(15,12,8,0.5)" }}>We&apos;ll use this to advise on layout and acoustics.</div>
                  <div className="flex gap-3 mt-2">
                    <button type="button" onClick={() => fileRef.current?.click()} className="text-[10px] uppercase tracking-wider" style={{ color: "#8A6A0F" }} data-testid="cons-photo-replace">Replace</button>
                    <button type="button" onClick={() => setF({ ...f, image_url: "" })} className="text-[10px] uppercase tracking-wider text-red-500" data-testid="cons-photo-remove">Remove</button>
                  </div>
                </div>
              </div>
            ) : (
              <button type="button" onClick={() => fileRef.current?.click()}
                className="w-full py-8 rounded-xl border-2 border-dashed transition text-sm flex flex-col items-center justify-center gap-2"
                style={{ borderColor: "rgba(15,12,8,0.14)", color: "rgba(15,12,8,0.6)" }}
                data-testid="cons-photo-upload">
                {uploading ? (
                  <><Loader2 className="w-5 h-5 animate-spin" /> Uploading…</>
                ) : (
                  <>
                    <ImageIcon className="w-6 h-6" style={{ color: "#8A6A0F" }} />
                    <div>Click to attach a room photo</div>
                    <div className="text-[10px] uppercase tracking-wider" style={{ color: "rgba(15,12,8,0.4)" }}>JPG · PNG · WebP · max 5MB · optional</div>
                  </>
                )}
              </button>
            )}
          </section>

          <div className="hairline"></div>

          {/* Notes */}
          <section>
            <label htmlFor="c-msg" className="block text-xs uppercase tracking-[0.18em] mb-2" style={{ color: "rgba(15,12,8,0.5)" }}>Anything else? <span className="normal-case text-[10px]">(brand preferences, existing gear, budget hint…)</span></label>
            <textarea id="c-msg" rows="5" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })}
              placeholder="Optional — the more we know, the better we curate." className="w-full" data-testid="cons-message"></textarea>
          </section>

          <button type="submit" disabled={loading}
            className="btn-gold w-full justify-center disabled:opacity-60" data-testid="cons-submit">
            <CalendarCheck className="w-4 h-4" /> {loading ? "Booking…" : "Book My Consultation"}
          </button>
          <p className="text-xs text-center" style={{ color: "rgba(15,12,8,0.4)" }}>We reply within 24 hours. No spam, no commitment.</p>
        </form>

        <aside className="space-y-5" data-testid="consultation-side">
          {[
            { t: "How it works", d: "1. Book below · 2. We call to schedule · 3. Showroom visit or in-home survey · 4. Bespoke proposal in writing." },
            { t: "Personal design specialist", d: "One of our senior specialists is assigned to your project — they know your room, your taste, your budget." },
            { t: "White-glove installation", d: "Certified engineers with 12+ years' experience. Fully insured, room-corrected on delivery." },
          ].map((x) => (
            <div key={x.t} className="glass rounded-2xl p-5">
              <div className="kicker mb-2">{x.t}</div>
              <p className="text-sm leading-relaxed" style={{ color: "rgba(15,12,8,0.65)", fontFamily: "'Manrope', sans-serif" }}>{x.d}</p>
            </div>
          ))}
          <div className="rounded-2xl p-5 border" style={{ borderColor: "rgba(138,106,15,0.35)", background: "rgba(138,106,15,0.05)" }}>
            <div className="kicker mb-2" style={{ color: "#8A6A0F" }}>Prefer to talk?</div>
            <a href="tel:+918018911999" className="block font-display text-xl" style={{ color: "var(--ink)" }}>+91 80189 11999</a>
            <a href="mailto:batraelectronics55@gmail.com" className="text-xs break-all hover:opacity-70" style={{ color: "rgba(15,12,8,0.6)" }}>batraelectronics55@gmail.com</a>
          </div>
        </aside>
      </div>
      </div>
    </div>
  );
}
